import { Button, TextField } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { useAccountLimitsContext } from "../context/account-limit-context";
import { libraryTemplateAtom } from "../discover/library/store";
import { brandToneAtom, queryAtom } from "../generate/store";
import { PiiGuidanceNotice } from "../generic/components/pii-guidance-notice";
import { historyAtomFamily, refineAtomFamily } from "./store";

import type { OmitUnion } from "../../utils/omit-union";
import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import type { RefineHistory } from "../generic/atoms/types/refine";
import type { FC } from "react";

export const AiRefineInput: FC<{
  tone: ContentTone | "custom";
  label?: string;
  config: OmitUnion<RefineHistory, "prompt">;
}> = ({ tone, label, config }) => {
  const [prompt, setPrompt] = useState("");
  const { enable_pii_guidance } = useAccountLimitsContext();

  const query = useAtomValue(queryAtom);
  const template = useAtomValue(libraryTemplateAtom);
  const customTone = useAtomValue(brandToneAtom);
  const key = {
    contentKey: tone,
    ...(tone === "custom" && customTone && { customTone }),
    ...(config.store === "generate" ? { query } : { template }),
  };
  const [history, setHistory] = useAtom(historyAtomFamily(key));
  const chat = useAtomValue(refineAtomFamily(history.slice(-1)?.[0]));

  const handleRefine = useCallback(() => {
    if (history.length === 0) setHistory([{ ...config, prompt }]);
    else {
      if (chat.state !== "hasData" || !chat.data) return;

      const newItem =
        history[0]!.store === "discover"
          ? {
              store: "discover" as const,
              type: "generated" as const,
              prompt,
              extra: { chatId: chat.data.chatId },
            }
          : {
              store: "generate" as const,
              type: "generated" as const,
              prompt,
              extra: { chatId: chat.data.chatId },
            };

      setHistory([...history, newItem]);
    }
    setPrompt("");
  }, [prompt, history, setHistory, chat.state, config]);

  return (
    <>
      {enable_pii_guidance && <PiiGuidanceNotice analyticsId="ai-refine-input" />}
      <TextField
        fullWidth
        multiline
        maxRows={3}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={label ?? "E.g. Add some emojis"}
        label={label ?? "Send a request"}
        slotProps={{
          input: {
            endAdornment: (
              <Button
                data-analytics-id="ai-refine-input-refine"
                onClick={handleRefine}
                variant="outlined"
                size="small"
                disabled={chat.state !== "hasData"}
              >
                Refine
              </Button>
            ),
          },
        }}
      />
    </>
  );
};
